/**
 * Water and ice (02 §4, mission 6). Crawling in water hides the ranger completely, until he
 * starts to drown. Cracked ice breaks under a runner always, under a walker half the time,
 * never under a crawler - and falling through is the end of him (01 §5).
 *
 * The garrison knows its own river: a man on his rounds picks his way between the cracks and
 * is never lost to them. A man hurrying after the ranger does not look down - which is the
 * lure. (So cracked ice carries no nav cost: a cost would steer the hunters round it too.)
 */

import { type Ctx, isFighter } from '../ctx';
import { damageEnemy, endMission, woundRanger } from '../damage';
import { destroyOutright, isAutomated } from '../enemies/specials/kinds';
import { waterAt, weakIceAt } from '../statics';
import { HAZARDS } from '../tuning';
import type { EnemyState } from '../types';
import { rangerOnFoot } from './terrain';

export function stepWater(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const h = w.hazards;
  if (!rangerOnFoot(w) || r.stance !== 'crawl' || !waterAt(statics.def, r.x, r.z)) {
    h.submergedTicks = 0;
    return;
  }
  h.submergedTicks++;
  // A held breath lasts so long; after that every further spell under costs a wound.
  const over = h.submergedTicks - HAZARDS.drownAfterTicks;
  if (over > 0 && over % HAZARDS.drownEveryTicks === 0) {
    ctx.events.push({ type: 'rangerHit', wounded: true });
    woundRanger(ctx, 1, 'drowning');
  }
}

/** On their rounds, or merely curious: men in these states watch their footing. */
const CAREFUL: ReadonlySet<EnemyState> = new Set<EnemyState>(['guard', 'patrol', 'return', 'suspicious']);

export function stepIce(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const def = statics.def;
  if (!def.waterBarriers.some((b) => b.surface === 'ice' && b.weakIce.length > 0)) return;
  const r = w.ranger;
  const h = w.hazards;

  if (rangerOnFoot(w)) {
    if (!weakIceAt(def, r.x, r.z)) h.iceHeld = false;
    else if (r.stance === 'stand') {
      // One roll per patch for a walking man; a runner goes through whatever it has held before.
      if (r.running || (!h.iceHeld && ctx.rng.chance(HAZARDS.walkerBreakChance))) {
        ctx.events.push({ type: 'iceBreak', x: r.x, z: r.z });
        endMission(ctx, 'kia', 'ice');
        return;
      }
      h.iceHeld = true;
    }
  }

  for (const e of w.enemies) {
    if (!isFighter(e) || !e.moving || e.mans !== null || CAREFUL.has(e.state)) continue;
    if (!weakIceAt(def, e.x, e.z)) continue;
    ctx.events.push({ type: 'iceBreak', x: e.x, z: e.z });
    if (isAutomated(e.kind)) destroyOutright(ctx, e, true);
    else {
      damageEnemy(ctx, e, 99, true);
      // He is under the ice: there is no body for a sentry to find.
      e.found = true;
    }
  }
}
